import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { ArrowLeft, Printer, DownloadSimple, PencilSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ViewQuotation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [quotation, setQuotation] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const qRes = await axios.get(`${API}/quotations/${id}`);
        setQuotation(qRes.data);
        const cRes = await axios.get(`${API}/companies`);
        setCompany(cRes.data.find(c => c.id === qRes.data.company_id));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: quotation ? `Quotation-${quotation.quotation_number}` : 'Quotation',
  });

  const handlePDFDownload = () => {
    const url = `${API}/quotations/${id}/pdf?letterhead=true`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="py-20 text-center text-stone-400 text-sm">Loading...</div>;
  if (!quotation) return <div className="text-center"><p className="text-stone-400 mb-4">Quotation not found</p></div>;

  const q = quotation;

  return (
    <div>
      {/* Top actions bar */}
      <div className="no-print mb-6">
        <button onClick={() => navigate('/')} data-testid="back-to-quotations"
          className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-800 text-sm font-medium mb-6 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h1 className="text-3xl text-stone-900" data-testid="view-quotation-title">{q.quotation_number}</h1>
          <div className="flex flex-wrap gap-2">
            <Link to={`/edit-quotation/${id}`} data-testid="edit-quotation-button"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-[#002FA7] bg-[#002FA7]/5 hover:bg-[#002FA7]/10 transition-all">
              <PencilSimple size={16} /> Edit
            </Link>
            <button onClick={() => handlePrint()} data-testid="print-quotation"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-stone-700 bg-stone-100 hover:bg-stone-200 transition-all">
              <Printer size={16} /> Print
            </button>
            <button onClick={handlePDFDownload} data-testid="download-pdf"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-white bg-[#002FA7] hover:bg-[#002480] transition-all active:scale-[0.97]">
              <DownloadSimple size={16} /> Download PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable content */}
      <div ref={printRef} className="bg-white border border-stone-200 print:border-0">
        <div className="p-8 sm:p-10 print:p-0">

          {/* Blank space at top for letterhead */}
          <div className="hidden print:block" style={{ height: '80px' }}></div>

          {/* Quotation meta */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-sm font-bold text-stone-900" data-testid="quotation-number-display">{q.quotation_number}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-stone-600">Date: <span className="font-semibold text-stone-900">{q.date}</span></p>
            </div>
          </div>

          {/* Customer */}
          {q.customer?.name && (
            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">To</p>
              <p className="text-sm font-bold text-stone-900">{q.customer.name}</p>
              {q.customer.address && <p className="text-xs text-stone-600 whitespace-pre-line">{q.customer.address}</p>}
              {q.customer.contact && <p className="text-xs text-stone-600">Contact: {q.customer.contact}</p>}
              {q.customer.email && <p className="text-xs text-stone-600">Email: {q.customer.email}</p>}
              {q.customer.gst_number && <p className="text-xs text-stone-600">GST: {q.customer.gst_number}</p>}
            </div>
          )}

          {/* Subject */}
          {q.subject && (
            <p className="text-sm font-semibold text-stone-800 mb-4">
              Sub.: {q.subject}
            </p>
          )}

          <p className="text-xs text-stone-600 mb-5">Sir/Madam, with reference to above, we are pleased to submit our quotation as follows:</p>

          {/* Items table */}
          <div className="mb-6 overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="quotation-items-table">
              <thead>
                <tr className="border-y-2 border-stone-900">
                  <th className="py-2.5 px-2 text-left font-bold text-stone-800 text-xs w-[40px]">S.No.</th>
                  <th className="py-2.5 px-2 text-left font-bold text-stone-800 text-xs">Description of Items</th>
                  <th className="py-2.5 px-2 text-center font-bold text-stone-800 text-xs w-[50px]">Qty.</th>
                  <th className="py-2.5 px-2 text-center font-bold text-stone-800 text-xs w-[50px]">Unit</th>
                  <th className="py-2.5 px-2 text-right font-bold text-stone-800 text-xs w-[100px]">Rate (Rs.)</th>
                  <th className="py-2.5 px-2 text-right font-bold text-stone-800 text-xs w-[110px]">Amount (Rs.)</th>
                </tr>
              </thead>
              <tbody>
                {q.items.map((item, idx) => (
                  <tr key={`item-${item.product_id}-${idx}`} className="border-b border-stone-200 align-top">
                    <td className="py-3 px-2 text-stone-600">{idx + 1}</td>
                    <td className="py-3 px-2">
                      <p className="font-semibold text-stone-900">{item.product_name}</p>
                      {item.description && <p className="text-xs text-stone-600 mt-0.5">{item.description}</p>}
                      {item.hsn_code && <p className="text-xs text-stone-500 mt-0.5">HSN: {item.hsn_code}</p>}
                      {item.features && item.features.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold text-stone-700 mb-1">Features:</p>
                          <ul className="text-xs text-stone-600 space-y-0.5 ml-1">
                            {item.features.map((f, fi) => f.trim() && (
                              <li key={`feat-${f.slice(0, 20)}-${fi}`} className="flex items-start gap-1.5">
                                <span className="text-stone-400 mt-0.5">&#8226;</span>
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {item.warranty && (
                        <p className="text-xs text-stone-700 mt-2"><span className="font-semibold">Warranty:</span> {item.warranty}</p>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center text-stone-700">{item.quantity}</td>
                    <td className="py-3 px-2 text-center text-stone-700">{item.unit || 'Nos.'}</td>
                    <td className="py-3 px-2 text-right text-stone-700">{item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="py-3 px-2 text-right font-semibold text-stone-900">{item.item_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-stone-900">
                  <td colSpan="4"></td>
                  <td className="py-2.5 px-2 text-right text-xs font-bold text-stone-800">Total Excl. GST</td>
                  <td className="py-2.5 px-2 text-right font-bold text-stone-900">{q.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr className="border-b border-stone-200">
                  <td colSpan="4"></td>
                  <td className="py-2 px-2 text-right text-xs font-bold text-stone-800">GST Total</td>
                  <td className="py-2 px-2 text-right font-bold text-stone-900">{q.gst_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
                {q.buyback_items && q.buyback_items.length > 0 && (
                  <>
                    <tr className="border-b border-stone-200">
                      <td colSpan="4"></td>
                      <td className="py-2 px-2 text-right text-xs font-bold text-red-600">Less: Buyback</td>
                      <td className="py-2 px-2 text-right font-bold text-red-600">-{(q.buyback_subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    {(q.buyback_gst_total || 0) > 0 && (
                      <tr className="border-b border-stone-200">
                        <td colSpan="4"></td>
                        <td className="py-2 px-2 text-right text-xs font-bold text-red-600">Less: Buyback GST</td>
                        <td className="py-2 px-2 text-right font-bold text-red-600">-{q.buyback_gst_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                  </>
                )}
                <tr className="bg-stone-50">
                  <td colSpan="4"></td>
                  <td className="py-3 px-2 text-right text-sm font-bold text-stone-900">{q.buyback_items && q.buyback_items.length > 0 ? 'Net Payable' : 'Grand Total'}</td>
                  <td className="py-3 px-2 text-right text-sm font-bold text-stone-900" data-testid="final-grand-total">{q.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Buyback Items */}
          {q.buyback_items && q.buyback_items.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-red-500 mb-3">Buyback Items (Deducted from Total)</h3>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-y border-red-300 bg-red-50/50">
                    <th className="py-2 px-2 text-left font-bold text-stone-800 text-xs w-[40px]">S.No.</th>
                    <th className="py-2 px-2 text-left font-bold text-stone-800 text-xs">Description</th>
                    <th className="py-2 px-2 text-center font-bold text-stone-800 text-xs w-[50px]">Qty.</th>
                    <th className="py-2 px-2 text-center font-bold text-stone-800 text-xs w-[50px]">Unit</th>
                    <th className="py-2 px-2 text-right font-bold text-stone-800 text-xs w-[100px]">Rate (Rs.)</th>
                    <th className="py-2 px-2 text-right font-bold text-stone-800 text-xs w-[110px]">Amount (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  {q.buyback_items.map((bb, idx) => (
                    <tr key={`bb-${bb.product_name}-${idx}`} className="border-b border-stone-200 align-top">
                      <td className="py-2 px-2 text-stone-600">{idx + 1}</td>
                      <td className="py-2 px-2">
                        <p className="font-semibold text-stone-900">{bb.product_name}</p>
                        {bb.description && <p className="text-xs text-stone-600 mt-0.5">{bb.description}</p>}
                      </td>
                      <td className="py-2 px-2 text-center text-stone-700">{bb.quantity}</td>
                      <td className="py-2 px-2 text-center text-stone-700">{bb.unit || 'Nos.'}</td>
                      <td className="py-2 px-2 text-right text-stone-700">{bb.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="py-2 px-2 text-right font-semibold text-red-600">{bb.item_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Terms & Conditions */}
          {q.terms_conditions && (
            <div className="border-t border-stone-300 pt-5 mb-8">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">Terms & Conditions</h3>
              <div className="text-xs text-stone-600 whitespace-pre-line leading-relaxed">{q.terms_conditions}</div>
            </div>
          )}

          {/* Signature — all same font size */}
          <div className="mt-10 text-xs text-stone-700">
            <p>Yours Faithfully,</p>
            {company && <p>For <span className="font-bold">{company.name.toUpperCase()}</span></p>}
            <div className="mt-10">
              <p className="font-bold">{q.user_name.toUpperCase()}</p>
              {q.user_phone && <p>Phone: {q.user_phone}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
